import { ActivityFilterKey, useWorkspaceAsset } from '@gripp/shared-logic';
import {
  ActivityFilterScreen,
  ActivityFilterScreenHandle,
  ActivityList,
  SlideAction,
  SlideLayout,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActivityFilters } from '../../activity/components/activityFilters';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { AssetRouteProvider, useAssetRouting } from '../assetRouteContext';
import { ALL_ACTIVITIES_QUERY_KEY, isActivitiesParent } from '../assetUtils';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

export const AssetDetail = () => {
  const { assetId, issueId } = useParams();
  const navigate = useNavigate();
  const filterRef = useRef<ActivityFilterScreenHandle>(null);
  const { workspaceAsset } = useWorkspaceAsset(assetId ?? '', !assetId);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [count, setCount] = useState<number>();
  const assetBasePath = issueId ? `/activities/${issueId}/asset/${assetId}` : `/assets/${assetId}`;

  if (!assetId) {
    return null;
  }

  const leftColumn = {
    title: 'assets.menu.title',
    mainComponent: (
      <AssetRouteProvider value={{
        basePath: assetBasePath,
        listPath: issueId ? '/activities' : '/assets',
        isWorkspaceMode: false,
        issueId,
      }}>
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ),
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    onBackButtonPress: () => navigate(issueId ? '/activities' : '/assets'),
    backButtonTransition: SlideAction.PREV,
    disableScroll: false,
  };

  const rightColumn = {
    title: 'assets.menu.title',
    rawTitle: workspaceAsset?.asset?.name,
    count,
    mainComponent: <RightColumn assetId={assetId} onCountChange={setCount} />,
    headerComponent: (
      <ActivityFilters
        assetId={assetId}
        showAddButton={false}
        onFilterChange={() => setIsFilterPanelOpen(true)}
      />
    ),
    showBackButton: true,
    disableScroll: true,
    onBackButtonPress: () => {
      navigate('../..', { relative: 'path' });
    },
    backButtonTransition: SlideAction.PREV,
  };

  const thirdColumn = isFilterPanelOpen ?{
    title: 'assets.menu.title',
    mainComponent: (
      <ActivityFilterScreen
        ref={filterRef}
        mode="page"
        isVisible={true}
        filterKey={ActivityFilterKey.AssetActivity}
        assetId={assetId}
        bottomBar={() => <></>}
      />
    ),
    headerComponent: (
      <HeaderResetButton
        onReset={() => filterRef.current?.onReset()}
      />
    ),
    showBackButton: true,
    disableScroll: true,
  } : undefined; 

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};

const RightColumn = ({
  assetId,
  onCountChange,
}: {
  assetId: string;
  onCountChange?: (count: number) => void;
}) => {
  const { onSlideChange } = useSlideLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { basePath } = useAssetRouting(assetId);
  // Read the context issue ID from the route (present on activities/:issueId/asset/:assetId/activity/all).
  const { issueId: contextIssueId } = useParams();

  const onIssueSelected = async (issueId: string) => {
    // Use contextIssueId (the outer activities route param) for the first segment,
    // not the newly-selected issueId — otherwise the original context is lost.
    const url = isActivitiesParent(location) && contextIssueId
      ? `/activities/${contextIssueId}/asset/${assetId}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`
      : `${basePath}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`;
    await onSlideChange(SlideAction.NEXT);
    navigate(url);
  };

  return (
    <ActivityList
      filterKey={ActivityFilterKey.AssetActivity}
      onIssueSelected={onIssueSelected}
      assetId={assetId}
      showAssetName={false}
      showImage={false}
      pageSize={50}
      showTopBar={false}
      onActivityCountChange={onCountChange}
    />
  );
};
